import { useContext, useState } from 'react';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import { Box, cardContentClasses, Grid, Stack, Tooltip, Typography } from '@mui/material';
import moment from 'moment';

import { allowOwnerShare, allowTenantShare, allowVendorShare } from 'Lib/constants/app/noteShareTypes';
import ContactTypes from 'Lib/constants/contacts/contactTypes';
import SidebarContext from 'Lib/contexts/SidebarContext';
import DeleteDialog from 'Lib/muiComponents/dialog/DeleteDialog';
import { getFileThumbnailUrl, openFile } from 'Lib/utilities/files';
import isEmpty from 'Lib/utilities/isEmpty';

import Card from '../../common/cards/Card';

import { NoteMetaData } from './styledComponents';

const DeleteModal = ({ isDeleting, toggle, open, handleDelete, note }) => {
	return (
		<DeleteDialog title="Delete Note" buttonText="Delete Note" open={open} isDeleting={isDeleting} toggle={toggle} onDelete={() => handleDelete(note)}>
			<Typography>Are you sure you want to delete this note?</Typography>
		</DeleteDialog>
	);
};

export const Note = ({ pinned, note: { fileAttachments = [], note, tags = [] }, isDeleting, handlePrint, handleDelete, toggleEdit, togglePin, toggleShare }) => {
	const {
		components: { User },
		sidebar
	} = useContext(SidebarContext);
	const { objectTypeID } = sidebar;

	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const NoteHeader = () => (
		<Typography sx={{ fontWeight: 'medium' }}>
			Note by <User id={note.createdByUserID} />
		</Typography>
	);

	const NoteBody = () => {
		const Tags = () => {
			if (isEmpty(tags)) return null;

			const tagList = tags.map(tag => `#${tag}`);

			return <Typography sx={{ fontWeight: 'medium', mt: 4, mb: 1 }}>{tagList.join(' ')}</Typography>;
		};

		const SharedWith = () => {
			const { isSharedWithTenant, isSharedWithVendor, isSharedWithOwner } = note;
			const sharedWithText = [isSharedWithTenant == 1 && 'Tenant', isSharedWithVendor == 1 && 'Vendor', isSharedWithOwner == 1 && 'Owner'].filter(Boolean).join(', ');

			if (isEmpty(sharedWithText)) return null;

			return (
				<Typography sx={{ fontSize: theme => `${theme.typography.pxToRem(12)}`, fontStyle: 'italic', letterSpacing: '0.4px' }}>
					Note being shared with <strong>{sharedWithText}</strong>
				</Typography>
			);
		};

		const Attachments = () => {
			return (
				<Grid container spacing={2} py={3}>
					{fileAttachments.map(({ file, fileAttachment }) => (
						<Grid item key={fileAttachment.fileAttachmentID} display="flex" onClick={() => openFile(file.fileID)}>
							<Tooltip title={file.title} placement="top-start" arrow>
								<Box
									sx={{
										borderRadius: 3,
										bgcolor: 'common.black',
										position: 'relative',
										'&:hover': { cursor: 'pointer', boxShadow: theme => `2px 2px 2px ${theme.palette.common.gray}` }
									}}>
									<Box
										component="img"
										src={getFileThumbnailUrl(file.fileID)}
										sx={{
											borderRadius: 3,
											height: 48,
											width: 48
										}}
									/>
								</Box>
							</Tooltip>
						</Grid>
					))}
				</Grid>
			);
		};

		return (
			<Stack pb={2}>
				<Box display="flex" alignItems="center">
					<FroalaEditorView model={stripOutAllForeignStylingFromHtml(note.note)} />
				</Box>
				<Tags />
				<Attachments />
				<SharedWith />
			</Stack>
		);
	};

	const ExpandedFooter = () => (
		<Stack>
			<NoteMetaData>
				Created {moment.utc(note.dateTimeCreated).format('MM-DD-YYYY')} by <User id={note.createdByUserID} />
			</NoteMetaData>
			{note.modifiedByUserID && (
				<NoteMetaData>
					Modified {moment.utc(note.dateTimeModified).format('MM-DD-YYYY')} by <User id={note.modifiedByUserID} />
				</NoteMetaData>
			)}
		</Stack>
	);

	const actions = () => {
		const actions = [
			{ label: 'Edit', onClick: toggleEdit },
			{ label: 'Delete', onClick: () => setDeleteModalOpen(true) },
			{ label: pinned ? 'Unpin Note' : 'Pin Note', onClick: togglePin },
			{ label: 'Print Note', onClick: handlePrint }
		];

		if (allowOwnerShare(objectTypeID)) {
			actions.push({ label: note.isSharedWithOwner == '0' ? 'Share with Owner' : 'Stop sharing with Owner', onClick: () => toggleShare(ContactTypes.Owner) });
		}

		if (allowTenantShare(objectTypeID)) {
			actions.push({ label: note.isSharedWithTenant == '0' ? 'Share with Tenant' : 'Stop sharing with Tenant', onClick: () => toggleShare(ContactTypes.Tenant) });
		}

		if (allowVendorShare(objectTypeID)) {
			actions.push({ label: note.isSharedWithVendor == '0' ? 'Share with Vendor' : 'Stop sharing with Vendor', onClick: () => toggleShare(ContactTypes.Vendor) });
		}

		return actions;
	};

	const localDateTime = moment.utc(note.dateTimeCreated).tz(moment.tz.guess());

	return (
		<Card
			title={<NoteHeader />}
			icon="more_vert"
			sx={{ mb: 4, py: 2, px: 4, overflowX: 'scroll' }}
			actions={actions}
			Footer={{
				expandedContent: () => <ExpandedFooter />,
				items: [
					{
						icon: 'calendar_month',
						content: () => (
							<Typography variant="caption">
								{localDateTime.isSame(moment(), 'day')
									? 'Today'
									: localDateTime.isSame(moment().subtract(1, 'day'), 'day')
										? 'Yesterday'
										: localDateTime.format('MMM D, YYYY')}
							</Typography>
						)
					},
					{
						icon: 'schedule',
						content: () => <Typography>{localDateTime.format('h:mm a z')}</Typography>
					}
				],
				sx: { [`& .${cardContentClasses.root}`]: { p: '0 !important' } }
			}}>
			<NoteBody />
			<DeleteModal open={deleteModalOpen} isDeleting={isDeleting} toggle={() => setDeleteModalOpen(!deleteModalOpen)} handleDelete={handleDelete} note={note} />
		</Card>
	);
};

/**
 * @param {string} input
 * @returns {string}
 */
function stripOutAllForeignStylingFromHtml(input) {
	if (!input || typeof input !== 'string') return '';

	// Convert the html entities to < and > respectively
	const unsafeString = input.replaceAll('&lt;', '<').replaceAll('&gt;', '>');

	const parser = new DOMParser();
	const doc = parser.parseFromString(unsafeString, 'text/html');

	const elementsWithStyle = doc.querySelectorAll('[style]');
	elementsWithStyle.forEach(element => {
		const s = element.style;

		// no absoulte, sticky, or fixed
		if (s.position && s.position != 'relative') {
			s.position = '';
		}

		// no float left, right, or whatever. only none
		if (s.float && s.float != 'none') {
			s.float = 'none';
		}
	});

	// Froala doesn't use divs in their operations AND notes don't allow for custom HTML
	// SO we can safely assume we will never need to preserve styled divs
	const divs = doc.querySelectorAll('div');
	divs.forEach(element => element.removeAttribute('style'));

	// Serialize the updated document back to an HTML string
	return doc.body.innerHTML;
}
